import { Component, OnInit, ViewChild, OnDestroy, AfterViewInit } from '@angular/core';
import { MatSort } from '@angular/material/sort';
import { MatPaginator } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { Subscription } from 'rxjs';
import moment from 'moment';

import { Licence } from '@classes/licence/licence';
import { LicenceTypeCode } from '@enums/licence-type-code.enum';
import { LicenceService } from '@services/licence.service';
import { ActivatedRoute, Router } from '@angular/router';
import { ReportService } from '@services/report.service';
import { PaginationService } from '@services/pagination.service';

@Component({
    selector: 'app-licences-list',
    templateUrl: './licences-list.component.html',
    styleUrls: ['./licences-list.component.scss']
})
export class LicencesListComponent implements OnInit, OnDestroy, AfterViewInit {
    @ViewChild(MatSort, { static: true }) sort: MatSort;
    @ViewChild(MatPaginator, {static: false}) paginator: MatPaginator;

    private licenceListSub: Subscription;

    licences: Licence[];
    displayedColumns: string[] =
        ['town', 'name', 'language', 'startDate', 'endDate', 'isTerminated', 'isPublic', 'type', 'actions'];
    dataSource: MatTableDataSource<Licence>;
    licenceType = LicenceTypeCode;

    isCreationMode: boolean;
    isLoading: boolean;
    filterValue: string;

    constructor(
        private licenceSrv: LicenceService,
        private route: ActivatedRoute,
        private router: Router,
        private reportSrv: ReportService,
        private paginationSrv: PaginationService
    ) {}

    ngOnInit() {
        this.getRouteData();
        this.initSubscriptions();
    }

    ngAfterViewInit(): void {
        this.paginationSrv.setText(this.paginator);

        this.dataSource.sortingDataAccessor = this.sortingDataAccessor;
        this.dataSource.sort = this.sort;
        this.dataSource.paginator = this.paginator;
    }

    endDateOutdated(date: Date): boolean {
        const momentDate = moment(date);
        const now = moment();

        if (momentDate < now) {
            return true;
        }

        return false;
    }

    onExport(): void {
        this.isLoading = true;

        this.reportSrv.generateXlsLicences()
            .subscribe(() => {
                this.isLoading = false;
            });
    }

    onApplyFilter(filterValue: string) {
        this.dataSource.filter = filterValue.trim().toLowerCase();
    }

    onCreate(): void {
        this.isCreationMode = true;
    }

    onEdit(code: string): void {
        this.router.navigate([code], { relativeTo: this.route });
    }

    onCreateFinished(): void {
        this.isCreationMode = false;
    }

    onBilling(): void {
        this.router.navigate(["billing"], { relativeTo: this.route });
    }

    private getRouteData(): void {
        this.route.data
            .subscribe(data => {
                this.licences = data.licences;
                this.initTable();
            });
    }

    private initTable(): void {
        this.dataSource = new MatTableDataSource(this.licences);
        this.initTableSorting();
        this.initTableFiltering();
    }

    private initSubscriptions(): void {
        this.licenceListSub = this.licenceSrv.licencesListChanged
            .subscribe(licences => {
                this.licences = licences;
                this.initTable();
            });
    }

    private initTableSorting(): void {
        this.dataSource.sortingDataAccessor = (item, property) => {
            switch (property) {
                case 'town': return item.organism.text.shortLabel.toLowerCase();
                case 'name': return item.text.shortLabel.toLowerCase();
                case 'language': return item.language.codeISO.toLowerCase();
                case 'type': return item.licenceType.text.shortLabel.toString();
                default: return item[property];
            }
        };
        this.dataSource.sortingDataAccessor = this.sortingDataAccessor;
        this.dataSource.sort = this.sort;
        this.dataSource.paginator = this.paginator;
    }

    private initTableFiltering(): void {
        this.dataSource.filterPredicate = (licence: Licence, filter: string) => {
            const reducer = (accumulator, key) => {
                switch(key) {
                    case 'organism': return accumulator + licence.organism.text.shortLabel;
                    case 'text': return accumulator + licence.text.shortLabel;
                    case 'language': return accumulator + licence.language.codeISO;
                    case 'licenceType': return accumulator + licence.licenceType.text.shortLabel;
                    default: return accumulator + licence[key];
                }
            };
            const dataStr: string = Object.keys(licence).reduce(reducer, '').toLowerCase();
            return dataStr.includes(filter.trim().toLowerCase());
        };
    }

    private sortingDataAccessor(item: Licence, property: string) : any {
        switch(property) {
            case 'town': return item.organism.text.shortLabel;
            case 'name': return item.text.shortLabel;
            case 'language': return item.language.codeISO;
            case 'isTerminated': return !!item.terminationDate;
            case 'isPublic': return item.organism.isPublic;
            case 'type': return item.licenceType.text.shortLabel;
            default: return item[property];
        }
    };

    ngOnDestroy() {
        if (this.licenceListSub) {
            this.licenceListSub.unsubscribe();
        }
    }
}
